/**
 * Wheel Configurator Tyres
 * ------------------------------------------------------------------------------
 * A script to load the associated tyres for an individual product.
 *
 * @namespace wheelConfigTyres
 */
import log from 'salvo-lite/log'

import {formatMoney} from '@shopify/theme-currency'
import axios from 'axios'

import {imageParameters, on} from '../../helpers/utils'
import {misc as icons} from '../../helpers/svg-map'

class WcTyres {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            productTitle: '[js-wheel-config-title]',
            carContainer: '[js-car-image-container]',
            carTyre: '[js-car-tyre]',
            productDescription: '[js-product-description]',
            tyreCarouselContainer: '[js-carousel=tyre]',
            tyreCarouselItem: '[js-tyre-item]',
            alloyPriceElement: '[js-alloy-price]',
            tyreDescription: '[js-tyre-description]',
            quantityInput: '[js-quantity-selector=input]',
            addToCart: '[js-ajax-cart=addToCart]',
            ...JSON.parse(elem.getAttribute('data-wc-tyres') || '{}')
        }

        /**
         * Node selectors.
         */
        this._nodeSelectors = {
            productTitle: document.querySelector(this._settings.productTitle),
            carContainer: document.querySelector(this._settings.carContainer),
            carTyre: document.querySelectorAll(this._settings.carTyre),
            productDescription: document.querySelector(this._settings.productDescription),
            tyreCarouselContainer: document.querySelector(this._settings.tyreCarouselContainer),
            alloyPriceElement: document.querySelectorAll(this._settings.alloyPriceElement),
            quantityInput: document.querySelector(this._settings.quantityInput),
            addToCart: document.querySelector(this._settings.addToCart)
        }

        /**
         * Global variables.
         */
        this._selectedItemPrice = 0
        this._newTotal = 0
        this._allTyreItems = []

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise accordion component.
     */
    onInit () {
        this._setListeners()
        this._loadTyreCarousel()
    }

    /**
     * Listen for events
     * 1. WheelConfig:tyresLoaded - fired when the tyres ajax call returns the HTML.
     * 2. WheelConfig:tyreChange - fired when the selected tyre changes.
     * 3. QuantitySelector-1:update - fired when the quantity selector value changes.
     */
    _setListeners () {
        window.Frame.EventBus.listen('WheelConfig:tyresLoaded', (response) => {
            this._getAllTyreItems()
            this._setChangeEvents()
            this._getSelectedInputPrice()
            this._updateAlloyPrice(1)
            this._updateTyreImages(response.dataset.imgSrc)
            this._updateTitle(response.dataset.title)
            this._updateAddToCart(response.dataset.availability)
        })

        window.Frame.EventBus.listen('WheelConfig:tyreChange', (response) => {
            this._updateAlloyPrice(response.quantity)
            this._updateProductDescription(response.description)
            this._updateTyreImages(response.image)
            this._updateTitle(response.title)
            this._updateAddToCart(response.availability)
        })

        window.Frame.EventBus.listen('QuantitySelector-1:update', (response) => {
            this._updateAlloyPrice(response)
        })
    }

    /**
     * Update the price HTML element, converted into money format.
     * @param {number} quantity the quantity value
     */
    _updateAlloyPrice (quantity) {
        this._newTotal = this._selectedItemPrice * quantity

        this._nodeSelectors.alloyPriceElement.forEach((item) => {
            item.innerHTML = formatMoney(this._newTotal, window.theme.moneyFormat)
            item.dataset.price = this._newTotal
        })
    }

    /**
     * Update the product title with the selected tyres title.
     * @param {string} title The product title
     */
    _updateTitle (title) {
        this._nodeSelectors.productTitle.innerHTML = title
    }

    /**
     * Update the description with the selected tyres description.
     * @param {string} desc the product description of the tyre.
     */
    _updateProductDescription (desc) {
        this._nodeSelectors.productDescription.innerHTML = desc
    }

    /**
     * Update the add to cart with the selected tyres availability.
     * @param {string} resp the product response of the tyre.
     */
    _updateAddToCart (availability) {
        if (availability === 'unavailable') {
            this._nodeSelectors.addToCart.querySelector('.wheel-config-form__submit-text').innerHTML = 'Sold Out'
            this._nodeSelectors.addToCart.disabled = true
        } else {
            this._nodeSelectors.addToCart.querySelector('.wheel-config-form__submit-text').innerHTML = 'Add to Cart'
            this._nodeSelectors.addToCart.disabled = false
        }
    }

    /**
     * Update the tyres on the car with the selected tyre item
     * @param {string} image image URL of tyre
     */
    _updateTyreImages (image) {
        this._nodeSelectors.carTyre.forEach((tyre) => {
            tyre.style.backgroundImage = `url('${imageParameters(image, {size: 'master'})}')`
        })
    }

    /**
     * Load tyre products if Tyre data exists.
     * Once loaded, run setChangeEvents _to listen for input changes.
     * Once loaded, run getSelectedItem _to set initial price value of selected tyre.
     */
    _loadTyreCarousel () {
        const searchString = this._nodeSelectors.tyreCarouselContainer.dataset.tyre

        if (searchString) {
            this._retrieveAssociatedTyreProducts(searchString)
                .then((response) => {
                    if (response) {
                        // populate tyre container with tyre items
                        this._nodeSelectors.tyreCarouselContainer.innerHTML = response

                        // setup node selector now that items exist
                        this._nodeSelectors.tyreCarouselItem = document.querySelectorAll(this._settings.tyreCarouselItem)

                        // emit event when the tyres have loaded
                        window.Frame.EventBus.emit('WheelConfig:tyresLoaded', this._getSelectedInput())

                        return
                    }

                    throw new Error('Response not found')
                })
                .catch((error) => error)
        }
    }

    /**
     * Create an array of all the tyre items.
     * @returns {Array} List of all tyre items
     */
    _getAllTyreItems () {
        this._nodeSelectors.tyreCarouselItem.forEach((item, index) => {
            this._allTyreItems[index] = item
        })
        return this._allTyreItems
    }

    /**
     * Loop over all the tyre items and return the selected input.
     * @returns {Object} The selected input node
     */
    _getSelectedInput () {
        const allItems = this._getAllTyreItems()
        let selectedInput

        allItems.forEach((item) => {
            if (item.querySelector('[js-tyre-input]').checked) {
                selectedInput = item
            }
        })

        return selectedInput
    }

    /**
     * Get the selected inputs price.
     */
    _getSelectedInputPrice () {
        const selectedInput = this._getSelectedInput()

        this._setPriceValue(selectedInput.dataset.price)
    }

    /**
     * Set the initial selected tyre price to a global var.
     * @param {number} val The initial selected tyre price
     */
    _setPriceValue (val) {
        this._selectedItemPrice = val
    }

    /**
     * Emit an event when tyre input is changed.
     */
    _setChangeEvents () {
        this._allTyreItems.forEach((item) => {
            on('change', item, () => {
                window.Frame.EventBus.emit('WheelConfig:tyreChange', this._aggregateItemData(item))
                window.Frame.EventBus.emit('WheelConfig:optionChanged')
            })
        })
    }

    /**
     * Put together the required data from the changed input into an object.
     * Update the tyre price global var.
     * @param {Object} item The node element of the input that has changed
     */
    _aggregateItemData (item) {
        const itemData = {
            title: item.dataset.title,
            image: item.dataset.imgSrc,
            description: item.querySelector(this._settings.tyreDescription).innerHTML,
            quantity: this._nodeSelectors.quantityInput.value,
            availability: item.dataset.availability
        }

        this._setPriceValue(item.dataset.price)
        return itemData
    }

    /**
     * Retrieve the associated trye products based on search query using SKU search.
     * @param {string} searchString url query string
     * @returns {string} combined string of all returned products
     */
    _retrieveAssociatedTyreProducts (searchString) {
        return new Promise((resolve, reject) => {
            axios.get(`/search?type=product&q=${searchString}&view=sku`)
                .then((response) => {
                    if (response) {
                        resolve(response.data.products.map((product, index) => {
                            return this._getTyreCarouselItemTemplate(product, index)
                        }).join(''))

                        return
                    }

                    throw new Error('No response found!')
                })
                .catch((error) => reject(error))
        })
    }

    /**
     * Split the product title on ', ' and return the sub string after the split.
     * @param {string} title the product title
     * @returns {string} the sub string after the split
     */
    _getSplitProductTitle (title) {
        const splitTitle = title.split(', ')
        return splitTitle[1]
    }

    /**
     * Get the HTML markup for the product cards.
     * @param {string} - product data got from axios response
     * @returns {string} Product HTML
     */
    _getTyreCarouselItemTemplate (product, index) {
        const productAvailable = (() => {
            return (product.available ? 'available' : 'unavailable')
        })()

        return `
        <div class="wheel-config-carousel__item ${productAvailable}" data-availability="${productAvailable}" data-title="${product.title.replace(/"/g, '&quot')}" data-price="${product.price}" data-img-src="${imageParameters(product.featured_image, {size: 'master'})}" js-carousel-item="tyre" js-tyre-item>
            <label for="tyre-type-${index}">
            <input class="wheel-config-carousel__radio visually-hidden" name="id" value="${product.variants[0].id}" type="radio" id="tyre-type-${index}" ${(index === 0) ? 'checked' : ''} data-price="${product.price}" data-img-src="${imageParameters(product.featured_image, {size: 'master'})}" js-tyre-input>
            <img
                class="wheel-config-carousel__thumb"
                src="${imageParameters(product.featured_image, {size: '70x70', crop: 'center'})}"
                alt=""
                srcset="
                ${imageParameters(product.featured_image, {size: '70x70', crop: 'center'})} 1x,
                ${imageParameters(product.featured_image, {size: '70x70', scale: 2, crop: 'center'})} 2x"
                sizes="auto"
            >
            <span class="wheel-config-carousel__tick">
                ${icons.tick}
            </span>
            <span class="wheel-config-carousel__text">${this._getSplitProductTitle(product.title)}</span>
            </label>
            <div class="wheel-config-carousel__description" js-tyre-description>
            ${this._getTyreDescription(product.description)}
            </div>
        </div>
        `
    }

    /**
     * Remove split characters from description.
     * @param {String} description the tyre's description.
     * @returns {String} only the ---DESCRIPTION--- part of the description.
     */
    _getTyreDescription (description) {
        if (!description.includes('---DESCRIPTION---')) {
            return description
        }

        let tyreDescription = description.split('<p>---DESCRIPTION---</p>')
        tyreDescription = tyreDescription[1]
        tyreDescription = tyreDescription.split('<p>---')
        tyreDescription = tyreDescription[0]

        return tyreDescription
    }

    /**
     * Expose public interface.
     */
    // return Object.freeze({
    //     init,
    // })
}

export default WcTyres



// WEBPACK FOOTER //
// ./src/resources/scripts/components/wheel-config/WcTyres.js